/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import { App } from '@capacitor/app';
import router from '../router';
import { removeNoNetworkModal } from './connectivity/removeNoNetworkModal';
import { getIsConnectedToInternet } from './connectivity/getIsConnectedToInternet';
import capacitorService from './capacitor.service';
import EventConst from '../constants/event.constant';
import Emitter from '../bus';
import AppConst from '../constants/app.constant';
import utilityService from './utility.service';
import EtdService from './etd.service';
import { loadMrmsDataRemote } from './mrms/loadMrmsDataRemote';
import stormTrackDataService from '../views/Mapview/stormTracks/stormTrackDataService';
import globeviewService from '../views/Mapview/Mapview.service';
import { hoursToMilliseconds } from './utils/hoursToMilliseconds';
import reportError from './error-handler/reportError';
import { resumeAppAuthCheck } from './aws/user/resumeAppAuthCheck';
import { doMagicLinkLogin } from './auth/doMagicLinkLogin';
import { asyncAuthFlow } from './auth/asyncAuthFlow';

App.addListener('appUrlOpen', async (data) => {
  console.log('App opened with URL:', data);
  await doMagicLinkLogin(data.url);
  await asyncAuthFlow({ fromMobileAppMagicLinkLogin: true });
});

let timeout;
const appStateService = () => {
  const content = {};
  content.pausedOn = null;
  content.appStateEventBinded = false;
  content.refreshAppScreen = () => {
    // reload current view
    EtdService.resetDepartureTime();
    loadMrmsDataRemote();
    stormTrackDataService.resetStormTrackService();
    globeviewService.resetRadarCanvasCollection();
    if (router
      && router.currentRoute
      && router.currentRoute.value
      && router.currentRoute.value.name) {
      Emitter.emit(
        EventConst.refreshViewRootscope + router.currentRoute.value.name,
        { isFromIdleWatch: true },
      );
    }
  };
  content.refreshAppScreenIfCacheExpire = () => {
    let timeElapsed;
    if (content.pausedOn) {
      timeElapsed = utilityService.getTimeElapsedInMiliseconds(content.pausedOn);
      if (timeElapsed >= AppConst.httpCacheAge) {
        console.log('refreshing screen idle');
        content.refreshAppScreen();
      }
    }
  };
  content.stopIdleWatcher = () => {
    clearTimeout(timeout);
  };

  function refreshIdleWatcher() {
    content.stopIdleWatcher();
    timeout = setTimeout(() => {
      refreshIdleWatcher();
      console.log('Refreshing as idle watcher expired and data needs refresh');
      content.refreshAppScreen();
    }, (AppConst.httpCacheAge + AppConst.idleTimeout));
  }
  content.attachDocumentClick = () => {
    document.addEventListener('click', refreshIdleWatcher);
  };
  content.bindIdleWatcher = () => {
    refreshIdleWatcher();
    content.attachDocumentClick();
  };
  content.onResume = () => {
    console.log('Application Resumed');
    // Handle the resume event
    /**
     * TODO: On resume add logic to recheck auth
     */
    resumeAppAuthCheck();
    if (capacitorService.isCapacitorMobileApp) {
      Emitter.emit(EventConst.appPaused, 'resume');
      refreshIdleWatcher();
    }
    content.refreshAppScreenIfCacheExpire();
  };
  content.onPause = () => {
    console.log('Application Paused');
    // Handle the pause event
    content.pausedOn = new Date();
    if (capacitorService.isCapacitorMobileApp) {
      Emitter.emit(EventConst.appPaused, 'paused');
      content.stopIdleWatcher();
    }
  };
  const appResumeAfterLong = () => {
    let timeElapsed;
    if (content.pausedOn) {
      timeElapsed = utilityService.getTimeElapsedInMiliseconds(content.pausedOn);
      if (timeElapsed >= hoursToMilliseconds(AppConst.hoursSleepToForceReload)) {
        if (capacitorService.isCapacitorMobileApp) {
          window.location.href = 'index.html';
        } else {
          window.location.reload();
        }
        return true;
      }
    }
    return false;
  };
  const capacitorApp = {
    onStateChange: (state) => {
      // state.isActive contains the active state
      console.log('App state changed. Is active?', state.isActive);
      if (!appResumeAfterLong()) {
        if (state.isActive) {
          doMagicLinkLogin().then(() => {});
          // content.onResume();
        } else {
          content.onPause();
        }
      }
    },
  };
  const web = {
    onResume: () => {
      if (!appResumeAfterLong()) {
        /**
         * TODO: On resume add logic to recheck auth
         */
        resumeAppAuthCheck();
        console.log('resume app');
        content.refreshAppScreenIfCacheExpire();
        if (getIsConnectedToInternet()) {
          removeNoNetworkModal();
        }
      }
    },
    onPause: () => {
      console.log('blur app');
      content.onPause();
    },
  };
  content.bindApplicationState = async () => {
    if (!content.appStateEventBinded) {
      content.appStateEventBinded = true;
      content.bindIdleWatcher();
      if (capacitorService.isCapacitorMobileApp) {
        App.addListener('appStateChange', capacitorApp.onStateChange);
        // Listen for serious plugin errors
        App.addListener('pluginError', (info) => {
          window.console.error('There was a serious error with a plugin', {}, info);
          reportError(info);
        });
      } else {
        window.addEventListener('focus', web.onResume, false);
        window.addEventListener('blur', web.onPause, false);
      }
    }
  };
  return content;
};
export default appStateService();
